/**
 * FeaturedTagsStrip — Sprint 3 (FE-11.5.C, Chunk 9.5 Phase B).
 *
 * Apple-Photos-style horizontal chip strip pinned above the Tag
 * layout's section list. Shows the top 5 tags by item count with
 * an optional emoji per common tag and the count below the label.
 *
 * Tapping a chip scrolls to that tag's section below. The scroll
 * mechanic is implemented via a ref + a callback the parent passes
 * — keeps this component decoupled from the parent's FlatList.
 *
 * Empty state — when no tagged items exist, the strip doesn't
 * render at all (the parent's FE-9.4.2 banner picks up the slack).
 */

import React, { useMemo } from 'react';
import {
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { fpColors } from '../../../styles/theme';
import type { BrowseSection } from '../groupItemsBy';

/**
 * Mapping from common tag names to emoji glyphs. Lower-cased tag
 * lookups; case-insensitive match. Falls back to no emoji if a
 * tag isn't in the dictionary — chip still renders, just text-only.
 */
const TAG_EMOJI_DICT: Record<string, string> = {
  // household
  kitchen: '🍴',
  bathroom: '🛁',
  bedroom: '🛏',
  garage: '🔧',
  garden: '🌳',
  basement: '📦',
  attic: '📦',
  closet: '👔',
  office: '💼',
  // categories
  tools: '🔧',
  tool: '🔧',
  appliance: '⚡',
  appliances: '⚡',
  electronics: '🔌',
  electronic: '🔌',
  food: '🥕',
  drink: '🥤',
  drinks: '🥤',
  clothing: '👕',
  clothes: '👕',
  furniture: '🪑',
  decor: '🖼',
  toy: '🧸',
  toys: '🧸',
  book: '📚',
  books: '📚',
  document: '📄',
  documents: '📄',
  // contractor / videographer
  hardware: '🔩',
  saw: '🪚',
  drill: '🔩',
  camera: '📷',
  cameras: '📷',
  light: '💡',
  lights: '💡',
  lighting: '💡',
  audio: '🎙',
  // misc
  baby: '🍼',
  pet: '🐾',
  pets: '🐾',
  car: '🚗',
  vehicle: '🚗',
  music: '🎵',
};

function emojiForTag(tag: string): string | null {
  const key = tag.trim().toLowerCase();
  return TAG_EMOJI_DICT[key] ?? null;
}

export interface FeaturedTagsStripProps {
  /** All tag sections from groupItemsBy('tag', ...). The strip
   *  renders ALL of them in count-desc order — no cap — letting the
   *  long tail be visually browsable (Apple Photos People & Pets
   *  precedent). Pass everything including 'Untagged' — we filter it
   *  out internally. */
  sections: BrowseSection[];
  /** Fired with the section.key when a chip is tapped. Parent uses
   *  this to scroll the FlatList to that section. */
  onChipPress: (sectionKey: string) => void;
  /** FE #258 — Optional cap.  Defaults to UNCAPPED (Infinity) so
   *  every tag is browsable.  Pass a number only when a caller
   *  needs the old top-N behavior (e.g. a small preview widget). */
  maxChips?: number;
}

/** FE #258 — over this many chips, switch from ScrollView to a
 *  virtualized horizontal FlatList so initial render stays cheap
 *  for users with 100+ tags.  Apple Photos People & Pets uses the
 *  same crossover. */
const VIRTUALIZE_THRESHOLD = 50;

export const FeaturedTagsStrip: React.FC<FeaturedTagsStripProps> = ({
  sections,
  onChipPress,
  maxChips = Infinity,
}) => {
  // Filter out "Untagged" + sort by item count desc.
  // FE #258 — previously this slice(0, 5) made tags #6+ invisible.
  // Default is now Infinity (uncapped); the caller can still pass an
  // explicit `maxChips` for small-preview use cases.
  const featured = useMemo(() => {
    const sorted = sections
      .filter((s) => s.title.toLowerCase() !== 'untagged')
      .sort((a, b) => b.data.length - a.data.length);
    return Number.isFinite(maxChips) ? sorted.slice(0, maxChips) : sorted;
  }, [sections, maxChips]);

  if (featured.length === 0) return null;

  const useVirtualized = featured.length > VIRTUALIZE_THRESHOLD;

  // Single chip render fn — shared between ScrollView path and FlatList path
  // so the visual is identical regardless of which container we mount.
  const renderChip = (section: BrowseSection) => {
    const emoji = emojiForTag(section.title);
    const count = section.data.length;
    return (
      <Pressable
        key={`featured-${section.key}`}
        onPress={() => onChipPress(section.key)}
        accessibilityRole="button"
        accessibilityLabel={`Jump to ${section.title} (${count} items)`}
        style={({ pressed }) => [styles.chip, pressed && styles.chipPressed]}
      >
        {emoji && <Text style={styles.chipEmoji}>{emoji}</Text>}
        <View style={styles.chipText}>
          <Text style={styles.chipLabel} numberOfLines={1}>
            {section.title}
          </Text>
          <Text style={styles.chipCount}>
            {count === 1 ? '1 item' : `${count} items`}
          </Text>
        </View>
      </Pressable>
    );
  };

  return (
    <View style={styles.wrap} accessibilityLabel="Featured tags">
      <Text style={styles.heading}>Featured tags</Text>
      {useVirtualized ? (
        // FE #258 — At 50+ tags the eager ScrollView starts to stutter
        // on the first paint.  Switch to a horizontal FlatList with
        // `initialNumToRender=20` (1.5 screen widths' worth) so the
        // off-screen tail mounts lazily.
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.scroll}
          data={featured}
          keyExtractor={(section) => `featured-${section.key}`}
          renderItem={({ item }) => renderChip(item)}
          initialNumToRender={20}
          maxToRenderPerBatch={10}
          windowSize={5}
          removeClippedSubviews
        />
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.scroll}
        >
          {featured.map(renderChip)}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    paddingTop: 4,
    paddingBottom: 8,
  },
  heading: {
    fontSize: 11,
    fontWeight: '700',
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    paddingHorizontal: 16,
    marginBottom: 6,
  },
  scroll: {
    paddingHorizontal: 16,
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    minWidth: 96,
  },
  chipPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
    borderColor: fpColors.primary,
  },
  chipEmoji: {
    fontSize: 22,
    marginRight: 8,
  },
  chipText: {
    flexShrink: 1,
  },
  chipLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: fpColors.onSurface,
    textTransform: 'capitalize',
  },
  chipCount: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
});

export { emojiForTag };
export default FeaturedTagsStrip;
